import { Button, Checkbox, Divider, Flex, Form, Space, Spin, Tooltip } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { adjustBookingPrice, adjustOrderPrice, getClientBookingDetails, getFeeSummary, getTripCustomFees } from "store/bookings"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import PriceAndReasonFields from "./PriceAndReasonFields"
import { DownOutlined, LoadingOutlined, UpOutlined } from "@ant-design/icons"
import { numberToCurrency } from "helpers/string"
import CustomDrawer from "ui/Drawer"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { Modal } from "ui/Modal"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import { calculateAdjustmentFeeBreakdown, CASH_BALANCE_PAYMENT_OPTIONS } from "utils/bookingCalculations"

const AdjustBookingPriceModal = ({
  onOpen,
  onClose,
  currentPrice,
  paidAmount,
  bookingUUID,
  orderUUID,
  tripUUID,
  isMarketplaceBooking,
  paymentOption
}) => {
  const [form] = Form.useForm()
  const [hasErrors, setHasErrors] = useState(true)
  const [newPrice, setNewPrice] = useState(null)
  const [feesExpanded, setFeesExpanded] = useState(false)
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.bookings.adjustBookingPrice)
  const { data: customFeesData, loading: customFeesLoading } = useSelector((state) => state.bookings.tripCustomFees)
  const { data: feeSummaryData, loading: feeSummaryLoading } = useSelector((state) => state.bookings.feeSummary)
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm", "md"].includes(breakpoint)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)

  useEffect(() => {
    if (!onOpen) return
    if (tripUUID) dispatch(getTripCustomFees(tripUUID))
    dispatch(getFeeSummary())
  }, [onOpen, tripUUID, dispatch])

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true)
      return
    }
    executeClose()
  }

  const executeClose = () => {
    setShowUnsavedModal(false)
    setHasUnsavedChanges(false)
    onClose()
  }

  const isValidNewPrice = (value) => {
    const numeric = Number.parseFloat(value)
    return value && !Number.isNaN(numeric) && numeric > 0 && Math.abs(numeric - currentPrice) >= 0.01
  }

  const handleSubmit = (data) => {
    const payload = {
      new_price: Number(data?.amount),
      reason: data?.reason,
      notify_client: isMarketplaceBooking ? true : data?.notify_client
    }
    // A whole order's price goes to the order, which splits it across its bookings.
    dispatch(orderUUID ? adjustOrderPrice(orderUUID, payload) : adjustBookingPrice(bookingUUID, payload))
      .then((response) => {
        if (response?.success) {
          toaster.success(response?.message)
          form.resetFields()
          setHasUnsavedChanges(false)
          onClose()
          if (bookingUUID) dispatch(getClientBookingDetails(bookingUUID))
        }
      })
      .catch((error) => {
        if (error?.data?.is_amount_lower) {
          setHasErrors(true)
          setNewPrice(null)
          form.setFields([{ name: "amount", errors: [error?.data?.message] }])
        }
      })
  }

  const handleFieldsChange = () => {
    const fieldErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    const allTouched = form.isFieldsTouched(["amount", "reason"])
    setHasErrors(fieldErrors || !allTouched)
    setHasUnsavedChanges(form.isFieldsTouched())
    const amount = form.getFieldValue("amount")
    setNewPrice(isValidNewPrice(amount) ? Number.parseFloat(amount) : null)
  }

  const priceDiff = newPrice !== null ? newPrice - currentPrice : null
  const feeBreakdown = calculateAdjustmentFeeBreakdown({ customFeesData, feeSummaryData, diff: priceDiff, paymentOption })
  const totalDiff = feeBreakdown ? priceDiff + feeBreakdown.totalFees : priceDiff
  const showCashFeeNote = priceDiff > 0 && CASH_BALANCE_PAYMENT_OPTIONS.includes(paymentOption)

  const modalContent = (
    <Spin spinning={loading} indicator={<LoadingOutlined spin />} size="large">
      <Space className="w-full" direction="vertical" size={24}>
        <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s" align="center" wrap>
          <Text color={colors.textSecondaryDark} fw={600}>
            {t("client.client_booking.current_price")}:
          </Text>
          <Text color={colors.textPrimary}>{numberToCurrency(currentPrice)}</Text>
          {newPrice !== null && (
            <>
              <Text color={colors.textSecondaryDark} fw={600}>
                → {t("client.client_booking.new_price")}:
              </Text>
              <Text color={colors.textPrimary}>{numberToCurrency(newPrice)}</Text>
            </>
          )}
        </Flex>

        {priceDiff !== null && priceDiff !== 0 && (
          <Spin spinning={customFeesLoading || feeSummaryLoading} indicator={<LoadingOutlined spin />}>
            <Space direction="vertical" size={8} className="w-full" style={{ backgroundColor: colors.surface1, padding: spacings.M }}>
              <Flex justify="space-between" align="center">
                <Text color={colors.textSecondaryDark} fw={600}>
                  {t("client.client_booking.price_difference")}
                </Text>
                <Text color={priceDiff > 0 ? colors.success : colors.error} fw={600}>
                  {priceDiff > 0 ? "+" : ""}
                  {numberToCurrency(priceDiff)}
                </Text>
              </Flex>

              {feeBreakdown && feeBreakdown.feeItems.length > 0 && (
                <>
                  <Flex
                    justify="space-between"
                    align="center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setFeesExpanded((prev) => !prev)}
                  >
                    <Flex gap={spacings.XXS} align="center">
                      <Text color={colors.textSecondaryDark}>{t("client.payment.labels.taxes_fees")}</Text>
                      {feesExpanded ? (
                        <UpOutlined style={{ fontSize: 10, color: colors.textSecondaryDark }} />
                      ) : (
                        <DownOutlined style={{ fontSize: 10, color: colors.textSecondaryDark }} />
                      )}
                    </Flex>
                    <Text color={colors.textPrimary}>+{numberToCurrency(feeBreakdown.totalFees)}</Text>
                  </Flex>

                  {feesExpanded && (
                    <Space direction="vertical" size={4} className="w-full" style={{ paddingLeft: spacings.S }}>
                      {feeBreakdown.feeItems.map((item) => (
                        <Flex key={item.label} justify="space-between" align="center">
                          <Text color={colors.textSecondaryDark} size="sm">
                            {item.label}
                          </Text>
                          <Text color={colors.textPrimary} size="sm">
                            +{numberToCurrency(item.amount)}
                          </Text>
                        </Flex>
                      ))}
                    </Space>
                  )}

                </>
              )}

              <Divider style={{ margin: `${spacings.XXS}px 0` }} />
              <Flex justify="space-between" align="center">
                <Text color={colors.textSecondaryDark} fw={600}>
                  {t("client.client_booking.total_difference")}
                </Text>
                <Text color={priceDiff > 0 ? colors.success : colors.error} fw={600}>
                  {priceDiff > 0 ? "+" : ""}
                  {numberToCurrency(feeBreakdown ? totalDiff : Math.abs(priceDiff))}
                </Text>
              </Flex>


              {showCashFeeNote && (
                <Text color={colors.textSecondaryDark} size="sm">
                  A platform fee on the increase will apply only if you collect it by card or payment link.
                </Text>
              )}
            </Space>
          </Spin>
        )}

        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          onFieldsChange={handleFieldsChange}
          initialValues={{ notify_client: true }}
        >
          <PriceAndReasonFields currentPrice={currentPrice} paidAmount={paidAmount} />
          <Form.Item name="notify_client" valuePropName="checked">
            <Checkbox disabled={isMarketplaceBooking}>
              <Tooltip title={isMarketplaceBooking ? t("client.client_booking.notify_client_marketplace_tooltip") : ""}>
                {t("client.client_booking.notify_client")}
              </Tooltip>
            </Checkbox>
          </Form.Item>
          <Flex gap={spacings.S} className="mt-3xl">
            <Button className="py-l px-xl" color="primary" variant="outlined" onClick={requestClose}>
              {t("global.cancel")}
            </Button>
            <Button type="primary" className="py-l px-xl" htmlType="submit" disabled={hasErrors}>
              {t("global.submit")}
            </Button>
          </Flex>
        </Form>
      </Space>
    </Spin>
  )

  return (
    <>
      {isMobile ? (
        <CustomDrawer open={onOpen} setOpen={requestClose} title={t("client.client_booking.adjust_booking_price")}>
          {modalContent}
        </CustomDrawer>
      ) : (
        <Modal centered title={t("client.client_booking.adjust_booking_price")} open={onOpen} onCancel={requestClose} width={800}>
          {modalContent}
        </Modal>
      )}
      <UnsavedChangesModal
        open={showUnsavedModal}
        onDiscard={executeClose}
        onKeepEditing={() => setShowUnsavedModal(false)}
        zIndex={1200}
      />
    </>
  )
}

AdjustBookingPriceModal.propTypes = {
  onOpen: PropTypes.bool,
  onClose: PropTypes.func,
  currentPrice: PropTypes.number,
  paidAmount: PropTypes.number,
  bookingUUID: PropTypes.string,
  // Set instead of bookingUUID to adjust a whole order (its bookings share the change).
  orderUUID: PropTypes.string,
  tripUUID: PropTypes.string,
  isMarketplaceBooking: PropTypes.bool,
  paymentOption: PropTypes.string
}

export default AdjustBookingPriceModal
